<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import { useUsers } from '@/composables/useUsers'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { detail, loading, error, actionLoading, actionError, fetchDetail, verifyEmail, deactivate } = useUsers()

const userId = Number(route.params.id)

// Confirm dialog state
const verifyEmailDialogOpen = ref(false)
const deactivateDialogOpen = ref(false)
const successMessage = ref<string | null>(null)

onMounted(() => fetchDetail(userId))

async function handleVerifyEmail(): Promise<void> {
  verifyEmailDialogOpen.value = false
  const success = await verifyEmail(userId)
  if (success) {
    successMessage.value = t('admin.users.verify_email_success')
    setTimeout(() => { successMessage.value = null }, 4000)
  }
}

async function handleDeactivate(): Promise<void> {
  deactivateDialogOpen.value = false
  const success = await deactivate(userId)
  if (success) {
    successMessage.value = t('admin.users.deactivate_success')
    setTimeout(() => { successMessage.value = null }, 4000)
  }
}
</script>

<template>
  <AdminLayout>
    <div class="space-y-6">
      <!-- Back link -->
      <button
        type="button"
        class="text-slate-400 hover:text-white text-sm flex items-center gap-1 transition-colors"
        @click="router.push({ name: 'admin.users.index' })"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        {{ t('admin.users.back') }}
      </button>

      <!-- Loading state -->
      <div v-if="loading" class="space-y-4">
        <div class="h-8 bg-slate-800 rounded w-1/3 animate-pulse" />
        <div class="h-4 bg-slate-800 rounded w-1/4 animate-pulse" />
      </div>

      <!-- Error / not found state -->
      <div
        v-else-if="error"
        class="bg-red-500/10 border border-red-500/30 rounded-2xl p-6"
      >
        <p class="text-red-300 text-sm">
          {{ error === 'admin.common.not_found' ? t('admin.users.not_found') : t('admin.common.error_loading') }}
        </p>
      </div>

      <!-- User detail -->
      <div v-else-if="detail" class="space-y-6">
        <!-- Success toast -->
        <div
          v-if="successMessage"
          class="bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-3 text-sm text-emerald-300"
          role="status"
        >
          {{ successMessage }}
        </div>

        <!-- Action error toast -->
        <div
          v-if="actionError"
          class="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-300"
          role="alert"
        >
          {{ actionError }}
        </div>

        <!-- Header / Profile card -->
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h1 class="text-2xl font-bold text-white">{{ detail.name }}</h1>
              <p class="text-slate-400 mt-1">{{ detail.email }}</p>
              <div class="mt-3 flex flex-wrap gap-2">
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="detail.email_verified
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-amber-500/20 text-amber-400'"
                >
                  {{ detail.email_verified ? t('admin.users.badge_verified') : t('admin.users.badge_unverified') }}
                </span>
                <span
                  v-if="detail.deactivated"
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-500/20 text-red-400"
                >
                  {{ t('admin.users.badge_deactivated') }}
                </span>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="flex gap-2 shrink-0">
              <button
                v-if="!detail.email_verified"
                type="button"
                :disabled="actionLoading"
                class="bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-3 py-1.5 text-sm transition-colors"
                @click="verifyEmailDialogOpen = true"
              >
                {{ t('admin.users.verify_email') }}
              </button>
              <button
                v-if="!detail.deactivated"
                type="button"
                :disabled="actionLoading"
                class="bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-3 py-1.5 text-sm transition-colors"
                @click="deactivateDialogOpen = true"
              >
                {{ t('admin.users.deactivate') }}
              </button>
            </div>
          </div>

          <!-- Profile details -->
          <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div v-if="detail.phone">
              <span class="text-slate-500">{{ t('admin.users.phone') }}:</span>
              <span class="text-slate-300 ml-2">{{ detail.phone }}</span>
            </div>
            <div v-if="detail.email_verified_at">
              <span class="text-slate-500">{{ t('admin.users.email_verified_at') }}:</span>
              <span class="text-slate-300 ml-2">{{ new Date(detail.email_verified_at).toLocaleString() }}</span>
            </div>
            <div v-if="detail.deactivated_at">
              <span class="text-slate-500">{{ t('admin.users.deactivated_at') }}:</span>
              <span class="text-red-300 ml-2">{{ new Date(detail.deactivated_at).toLocaleString() }}</span>
            </div>
            <div>
              <span class="text-slate-500">{{ t('admin.users.preferred_language') }}:</span>
              <span class="text-slate-300 ml-2">
                {{ detail.preferred_language ? t(`admin.users.languages.${detail.preferred_language}`) : t('admin.users.preferred_language_not_set') }}
              </span>
            </div>
            <div>
              <span class="text-slate-500">{{ t('admin.users.registrations') }}:</span>
              <span class="text-slate-300 ml-2">{{ detail.registrations_count }}</span>
            </div>
            <div>
              <span class="text-slate-500">{{ t('admin.users.created_at') }}:</span>
              <span class="text-slate-300 ml-2">{{ new Date(detail.created_at).toLocaleDateString() }}</span>
            </div>
            <div>
              <span class="text-slate-500">{{ t('admin.users.last_login_at') }}:</span>
              <span class="text-slate-300 ml-2" data-testid="last-login-value">
                {{ detail.last_login_at ? new Date(detail.last_login_at).toLocaleString() : t('admin.users.never_logged_in') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Organisations section -->
        <div class="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
          <div class="px-6 py-4 border-b border-slate-700">
            <h2 class="text-lg font-semibold text-white">{{ t('admin.users.organisations_section') }}</h2>
          </div>
          <div v-if="detail.organisations.length === 0" class="px-6 py-8 text-center text-slate-400 text-sm">
            {{ t('admin.users.no_organisations') }}
          </div>
          <table v-else class="w-full text-sm">
            <thead>
              <tr class="border-b border-slate-700">
                <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.name') }}</th>
                <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.slug') }}</th>
                <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.member_role') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="org in detail.organisations"
                :key="org.id"
                class="border-b border-slate-700 last:border-0"
              >
                <td class="px-4 py-3 text-white">{{ org.name }}</td>
                <td class="px-4 py-3 text-slate-400 font-mono text-xs">{{ org.slug }}</td>
                <td class="px-4 py-3 text-slate-300 capitalize">{{ org.role ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Verify email confirm dialog -->
    <ConfirmDialog
      :open="verifyEmailDialogOpen"
      :title="t('admin.users.verify_email_confirm_title')"
      :body="t('admin.users.verify_email_confirm_body')"
      :confirm-label="t('admin.users.verify_email')"
      :loading="actionLoading"
      :danger="false"
      @update:open="verifyEmailDialogOpen = $event"
      @confirm="handleVerifyEmail"
    />

    <!-- Deactivate confirm dialog -->
    <ConfirmDialog
      :open="deactivateDialogOpen"
      :title="t('admin.users.deactivate_confirm_title')"
      :body="t('admin.users.deactivate_confirm_body')"
      :confirm-label="t('admin.users.deactivate')"
      :loading="actionLoading"
      :danger="true"
      @update:open="deactivateDialogOpen = $event"
      @confirm="handleDeactivate"
    >
      <template #warning>
        {{ t('admin.users.deactivate_confirm_body') }}
      </template>
    </ConfirmDialog>
  </AdminLayout>
</template>
